import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { fetchPendingLegalDocuments, acceptLegalDocuments } from 'src/redux/identity'

const LegalDocumentType = {
  TermsOfService: 1,
  PrivacyPolicy: 2,
  EmployerServiceAgreement: 3,
}

const documentNames = {
  [LegalDocumentType.TermsOfService]: 'Terms of Service',
  [LegalDocumentType.PrivacyPolicy]: 'Privacy Policy',
  [LegalDocumentType.EmployerServiceAgreement]: 'Employer Service Agreement',
}

const documentRoutes = {
  [LegalDocumentType.TermsOfService]: '/tac',
  [LegalDocumentType.PrivacyPolicy]: '/privacy',
  [LegalDocumentType.EmployerServiceAgreement]: '/employer-terms',
}

const AcceptLegalDocumentsModal = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const { signedIn, pendingLegalDocuments = [], pendingLegalDocumentsError, acceptingLegalDocuments } = useSelector(state => state.identity)

  useEffect(() => {
    if (signedIn) {
      dispatch(fetchPendingLegalDocuments())
    }
  }, [signedIn, dispatch])

  const handleAccept = () => {
    dispatch(acceptLegalDocuments())
  }

  const hiddenRoutes = ['/tac', '/privacy', '/employer-terms']
  if (hiddenRoutes.includes(location.pathname)) {
    return null
  }

  if (!signedIn) {
    return null
  }

  const dialogProps = {
    open: true,
    disableEscapeKeyDown: true,
    fullScreen,
    fullWidth: true,
    maxWidth: 'sm',
    sx: { zIndex: theme.zIndex.modal + 2 },
    slotProps: {
      backdrop: {
        sx: { backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0, 0, 0, 0.3)' },
      },
    },
  }

  if (pendingLegalDocumentsError) {
    return (
      <Dialog {...dialogProps}>
        <DialogTitle
          variant='h5'
          sx={{
            color: 'primary.dark',
            fontWeight: 700,
            pt: 4,
            pb: 0,
            px: 3,
          }}
        >
          Updated Terms &amp; Policies
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, pb: 3, px: 3 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
            Something went wrong loading the terms. Please try again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.100', px: 3, py: 2 }}>
          <Button
            variant='contained'
            fullWidth
            onClick={() => dispatch(fetchPendingLegalDocuments())}
            sx={{ bgcolor: 'primary.dark', '&:hover': { bgcolor: 'primary.dark', opacity: 0.9 } }}
          >
            Retry
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (pendingLegalDocuments.length === 0) {
    return null
  }

  return (
    <Dialog {...dialogProps}>
      <DialogTitle
        variant='h5'
        sx={{
          color: 'primary.dark',
          fontWeight: 700,
          pt: 4,
          pb: 0,
          px: 3,
        }}
      >
        Updated Terms &amp; Policies
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5, pb: 3, px: 3 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
          We&rsquo;ve updated our legal documents. Please review and accept to continue.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {pendingLegalDocuments.map(doc => (
            <Box
              key={doc.id}
              component='a'
              href={documentRoutes[doc.documentType]}
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1.5,
                bgcolor: 'grey.50',
                textDecoration: 'none',
                color: 'text.primary',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                },
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: 'primary.dark', fontSize: 22 }} />
              <Typography variant='body1' sx={{ flex: 1, fontWeight: 500 }}>
                {documentNames[doc.documentType] || `Document ${doc.documentType}`}
              </Typography>
              <OpenInNewIcon sx={{ color: 'grey.500', fontSize: 18 }} />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'grey.100', px: 3, py: 2 }}>
        <Button
          variant='contained'
          fullWidth
          onClick={handleAccept}
          disabled={acceptingLegalDocuments}
          sx={{ bgcolor: 'primary.dark', '&:hover': { bgcolor: 'primary.dark', opacity: 0.9 } }}
        >
          I Accept
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AcceptLegalDocumentsModal
